import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { Checkbox, Drawer, Form, Input, Tooltip, Col, Row, notification } from 'antd'
import styles from './ApiKeyDrawer.module.scss'
import { ApiKeyItemModel } from '@/services/apis/setting/module'
import { locale, useTranslation } from '@/services/i18n'
import openApi, { ApiKeyRequestParams } from '@/services/apis/openApi'
import UploadRsa from '../UploadRsa/UploadRsa'
import TextArea from 'antd/es/input/TextArea'
import { ApiKeyModelEnum, ApiKeyModelMap } from '@/constants/enum'
import { CheckboxValueType } from 'antd/es/checkbox/Group'
import { checkIsPrivateKey, formatRSAPubKey } from '@/utils/setting'
import DrawerFooter from '@/components/DrawerFooter'
import MyModal from '@/components/Modal'
import { CloseOutlined } from '@ant-design/icons'

const ApiKeyDrawer = forwardRef(
  (
    props: {
      success: Function
      data: ApiKeyItemModel
    },
    ref: any
  ) => {
    const { t } = useTranslation()
    const { data } = props
    const [open, setOpen] = useState(false)
    const [type, setType] = useState('')
    const [api, contextHolder] = notification.useNotification()
    const [tooltipIsOpen, setTooltipIsOpen] = useState(false)
    const [form] = Form.useForm()

    // 监听pubkey的变化
    const [timeId, setTimeId] = useState<NodeJS.Timeout | null>(null)
    const pubKey = Form.useWatch('pubKey', form)
    function onPubkeyChanged(newVal: string) {
      const isPrivKey = checkIsPrivateKey(newVal)
      if (isPrivKey) {
        api.warning({
          message: t('api.privKeyCheck'),
          placement: 'top',
          duration: 3,
          style: {
            width: 380,
            height: 48,
            backgroundColor: '#f0f9eb',
            fontSize: '12px',
            padding: '15px',
          },
          closeIcon: null,
        })
        setFormData({
          ...formData,
          apiPubKey: '',
        })
      } else {
        const formatedVal = formatRSAPubKey(newVal)
        if (formatedVal !== newVal) {
          setTooltipIsOpen(true)
          let timer = setTimeout(() => {
            setTooltipIsOpen(false)
          }, 3000)
          setTimeId(timer)
        }
        setFormData({
          ...formData,
          apiPubKey: formatedVal,
        })
      }
    }
    useEffect(() => {
      onPubkeyChanged(pubKey)
      return () => {
        if (timeId) {
          clearInterval(timeId)
        }
      }
    }, [pubKey])

    const [isDisabled, setIsDisabled] = useState(true)
    const [formData, setFormData] = useState<ApiKeyRequestParams>({
      apiName: '',
      apiPubKey: '',
      ips: '',
      ipAddrList: [],
      permissionModeKeys: [],
    })

    const { success } = props
    useImperativeHandle(ref, () => ({
      openDrawer() {
        setOpen(true)
      },
    }))
    function renderData() {
      if (data?.userKey) {
        setType('update')
        setIsDisabled(false)
        setFormData({
          userKey: data?.userKey,
          apiName: data?.apiName as string,
          apiPubKey: data?.userPublicKey as string,
          ips: data?.ips.join(','),
          permissionModeKeys: data?.permissionModeKeys as string[],
          ipAddrList: [],
        })
      } else {
        setType('add')
        setFormData({
          apiName: '',
          apiPubKey: '',
          ips: '',
          ipAddrList: [],
          permissionModeKeys: [],
        })
        setIsDisabled(true)
      }
    }
    useEffect(() => {
      renderData()
    }, [data])

    const handleTitle = (
      <div className={styles.title}>{type === 'update' ? t('api.updateApi') : t('api.addApi')}</div>
    )
    const [loading, setLoading] = useState(false)
    const handleSubmit = async () => {
      setLoading(true)
      if (type === 'update') {
        await handleUpdate()
      } else {
        await handleAdd()
      }
      handleFooterCancel()
      setLoading(false)
    }
    const handleUpdate = async () => {
      const res = await openApi.updateApiKey(
        formData?.userKey as string,
        formData.ips?.split(',') as string[],
        formData.permissionModeKeys
      )
      if (res.success) {
        window.shMessage.success(t('api.success'))
        success()
        setOpen(false)
      }
    }
    const handleAdd = async () => {
      const params: ApiKeyRequestParams = {
        ...formData,
        ipAddrList: formData.ips?.split(',') as string[],
      }
      params.apiPubKey = formatRSAPubKey(params.apiPubKey)
      const res = await openApi.createApiKey(params)
      if (res.success) {
        window.shMessage.success(t('api.success'))
        success()
        setOpen(false)
      }
    }
    // 点击取消时候恢复数据
    function handleFooterCancel() {
      if (type === 'add') {
        setFormData({
          apiName: '',
          apiPubKey: '',
          ips: '',
          ipAddrList: [],
          permissionModeKeys: [],
        })
        form.resetFields()
      } else {
        setFormData({
          userKey: data?.userKey,
          apiName: data?.apiName as string,
          apiPubKey: data?.userPublicKey as string,
          ips: data?.ips.join(','),
          permissionModeKeys: data?.permissionModeKeys as string[],
          ipAddrList: [],
        })
      }
      setOpen(false)
    }
    const handleFooter = (
      <DrawerFooter
        onCancel={handleFooterCancel}
        cancelText={t('common.cancel') as string}
        disabledOk={isDisabled}
        okText={t('common.confirm') as string}
        onOk={handleSubmit}
        isLoading={loading}
      ></DrawerFooter>
    )

    const uploadSuccess = (keyStr: string) => {
      setFormData({
        ...formData,
        apiPubKey: keyStr,
      })
      form.setFieldValue('pubkey', keyStr)
    }
    // 验证ip地址规则
    const validateIPAddress = (rule: any, value: string, callback: any) => {
      if (value === '') {
        let message = t('rule.emptyIP')
        return Promise.reject(new Error(message))
      }
      const ipList = value.split(',')
      const regexIPv4 = /^([0-9]{1,3}\.){3}[0-9]{1,3}$/
      const regexIPv6 = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/

      for (let i = 0; i < ipList.length; i++) {
        if (!regexIPv4.test(ipList[i]) && !regexIPv6.test(ipList[i])) {
          let message = t('rule.CountIp')
          return Promise.reject(new Error(message))
        }
      }

      return Promise.resolve()
    }

    const handleFormChange = () => {
      // 只有所有的表单项都被输入之后才会触发规则判断按钮状态并且是点击添加
      if (form.isFieldsTouched(['name', 'ip'], true) && type === 'add') {
        form
          .validateFields()
          .then(res => {
            setIsDisabled(false)
          })
          .catch(err => {
            setIsDisabled(true)
          })
      }
      // 只有所有的表单项都被输入之后才会触发规则判断按钮状态并且是点击修改
      if (type === 'update' && form.isFieldsTouched(['ip'], true)) {
        form
          .validateFields(['ip'])
          .then(res => {
            setIsDisabled(false)
          })
          .catch(err => {
            setIsDisabled(true)
          })
      }
    }
    // 多选框的逻辑
    const checkBoxChange = (checkedValues: CheckboxValueType[]) => {
      setFormData({
        ...formData,
        permissionModeKeys: checkedValues as string[],
      })
    }
    // 表单的逻辑
    const handleChange = (e: any) => {
      const { name, value } = e.target
      if (name !== 'apiPubKey') {
        setFormData({
          ...formData,
          [name]: value,
        })
      }
    }
    // 弹出框的逻辑
    const handleAuditChange = (value: any) => {
      MyModal.confirm({
        title: t('api.audit.title'),
        content: <div>{t('api.audit.content')}</div>,
        onOk: handleOK,
        onCancel: handleCancel,
      })
      // setModalOpen(true)
    }
    const handleCancel = (close: any) => {
      // 将审核点击取消
      const res = formData.permissionModeKeys.filter(item => {
        return item !== 'AUDIT_TRADE'
      })
      setFormData({
        ...formData,
        permissionModeKeys: res,
      })
      close()
    }
    const handleOK = (close: any) => {
      close()
    }

    return (
      <Drawer
        title={handleTitle}
        footer={handleFooter}
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        key="right"
        width={550}
        destroyOnClose={true}
        closeIcon={<CloseOutlined className={styles.drawerClose} onClick={handleFooterCancel} />}
        maskClosable={false}
      >
        {contextHolder}
        <Form
          form={form}
          name="basic"
          onChange={handleFormChange}
          style={{ marginTop: '-10px' }}
          className={styles.formContainer}
        >
          <Form.Item
            name="name"
            rules={[
              { required: true, message: t('rule.emptyName') as string },
              {
                min: 1,
                max: 30,
                message: t('rule.CountName') as string,
              },
            ]}
          >
            <div>
              <div className={styles.label}>{t('api.name')}</div>
              <Input
                placeholder={t('api.namePlaceholder') as string}
                name="apiName"
                value={formData.apiName}
                onChange={handleChange}
                disabled={type === 'update'}
              />
            </div>
          </Form.Item>
          <Form.Item name="pubKey">
            <div>
              <div className={styles.label}>
                {t('api.yourPubkey')}
                <a
                  href={
                    locale === 'zh-CN'
                      ? 'https://docs.safeheron.com/api/zh.html#API%20%E9%89%B4%E6%9D%83'
                      : 'https://docs.safeheron.com/api/en.html#API%20Authentication'
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                  className={styles.link}
                >
                  {t('api.how')}
                </a>
                <UploadRsa success={uploadSuccess} isShow={type === 'add'}></UploadRsa>
              </div>
              <div>
                <Tooltip
                  title={t('api.pubkeyAuto')}
                  open={tooltipIsOpen}
                  overlayStyle={{ fontSize: '12px' }}
                >
                  <TextArea
                    value={formData.apiPubKey}
                    onChange={handleChange}
                    name="apiPubKey"
                    rows={3}
                    placeholder={t('api.yourPubkeyPlaceholder') as string}
                    disabled={!(type === 'add')}
                    style={{ fontSize: '12px' }}
                  ></TextArea>
                </Tooltip>
              </div>
            </div>
          </Form.Item>
          <Form.Item name="ip" rules={[{ validator: validateIPAddress }]}>
            <div>
              <div className={styles.label}>
                {t('api.ipWhite')}({t('api.ipWhiteDesc')})
              </div>
              <TextArea
                value={formData.ips}
                name="ips"
                onChange={handleChange}
                rows={3}
                placeholder={t('api.ipWhitePlaceholder') as string}
              ></TextArea>
            </div>
          </Form.Item>
          <Form.Item>
            <div>
              <div className={styles.label}>{t('api.permissionSetting')}</div>
              <div>
                {/* 第一个样式自定义禁止状态 */}
                <Checkbox
                  checked
                  value="ALL_READ"
                  className={styles.readCheck}
                  style={{ marginBottom: '10px' }}
                >
                  {ApiKeyModelMap[ApiKeyModelEnum.ALL_READ]}
                </Checkbox>
                <br />
                <Checkbox.Group onChange={checkBoxChange} value={formData.permissionModeKeys}>
                  <Row>
                    <Col span={24} style={{ marginBottom: '10px' }}>
                      <Checkbox
                        value="CREATE_TRADE_OF_API_CREATE_ASSETS"
                        disabled={
                          formData.permissionModeKeys.includes('AUDIT_TRADE') &&
                          !formData.permissionModeKeys.includes('CREATE_TRADE_OF_API_CREATE_ASSETS')
                        }
                      >
                        {ApiKeyModelMap[ApiKeyModelEnum.CREATE_TRADE_OF_API_CREATE_ASSETS]}
                      </Checkbox>
                    </Col>

                    <Col span={24} style={{ marginBottom: '10px' }}>
                      <Checkbox
                        value="CREATE_TRADE_OF_USER_CREATE_ASSETS"
                        disabled={
                          formData.permissionModeKeys.includes('AUDIT_TRADE') &&
                          !formData.permissionModeKeys.includes(
                            'CREATE_TRADE_OF_USER_CREATE_ASSETS'
                          )
                        }
                      >
                        {ApiKeyModelMap[ApiKeyModelEnum.CREATE_TRADE_OF_USER_CREATE_ASSETS]}
                      </Checkbox>
                    </Col>
                    <Col span={24} style={{ marginBottom: '10px' }}>
                      <Checkbox value="MANAGEMENT_VAULT_ASSETS">
                        {ApiKeyModelMap[ApiKeyModelEnum.MANAGEMENT_VAULT_ASSETS]}
                      </Checkbox>
                    </Col>
                    <Col span={24} style={{ marginBottom: '10px' }}>
                      <Checkbox
                        value="AUDIT_TRADE"
                        disabled={
                          formData.permissionModeKeys.includes(
                            'CREATE_TRADE_OF_API_CREATE_ASSETS'
                          ) ||
                          formData.permissionModeKeys.includes(
                            'CREATE_TRADE_OF_USER_CREATE_ASSETS'
                          ) ||
                          formData.permissionModeKeys.includes('AUDIT_TRADE')
                        }
                        onChange={handleAuditChange}
                      >
                        {ApiKeyModelMap[ApiKeyModelEnum.AUDIT_TRADE]}
                      </Checkbox>
                    </Col>
                    <Col span={24}>
                      {(formData.permissionModeKeys.includes('CREATE_TRADE_OF_API_CREATE_ASSETS') ||
                        (formData.permissionModeKeys.includes(
                          'CREATE_TRADE_OF_USER_CREATE_ASSETS'
                        ) &&
                          !formData.permissionModeKeys.includes('AUDIT_TRADE'))) && (
                        <div style={{ fontSize: '12px' }}>({t('api.audit.info')})</div>
                      )}
                    </Col>
                  </Row>
                </Checkbox.Group>
              </div>
            </div>
          </Form.Item>
        </Form>
      </Drawer>
    )
  }
)

export default ApiKeyDrawer
